import React, { useEffect, useState } from "react"
import {
  Modal, Tag, Popover, Tooltip, Spin, message, Button, Image, Space
} from "antd"
import {
  AppstoreOutlined,
  TagOutlined,
  InboxOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  BarcodeOutlined,
  DeploymentUnitOutlined,
  EyeOutlined,
} from "@ant-design/icons"
import { useRegister } from "../../contexts/RegisterContext"
import { useCompany } from "../../contexts/CompanyContext"
import { useStore } from "../../contexts/StoreContext"
import { useSecureRequest } from "../../hooks/useSecureRequest"
import { TStorageItemRS, TInventoryFirestore } from "../../models/storageModel"

// ─── Estilos ──────────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: "#8c8c8c",
  marginBottom: 2,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
}
const valueStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#262626",
  fontWeight: 500,
}
const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  padding: "8px 0",
  borderBottom: "1px solid #f0f0f0",
}

const InfoRow = ({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode
  label: string
  children: React.ReactNode
}) => (
  <div style={rowStyle}>
    <div style={{ paddingTop: 2, color: "#bfbfbf", flexShrink: 0 }}>{icon}</div>
    <div style={{ flex: 1 }}>
      <div style={labelStyle}>{label}</div>
      <div style={valueStyle}>{children}</div>
    </div>
  </div>
)

interface ViewItemModalProps {
  visible: boolean
  item: TStorageItemRS | null
  onCancel: () => void
  onSuccess?: () => void
  canEdit?: boolean
}

export const ViewItemModal: React.FC<ViewItemModalProps> = ({
  visible,
  item: initialItem,
  onCancel,
  canEdit = false,
}) => {
  const { register } = useRegister()
  const { company } = useCompany()
  const { store } = useStore()
  const { securePost } = useSecureRequest()

  const [spinning, setSpinning] = useState(false)
  const [item, setItem] = useState<TStorageItemRS | null>(null)
  const [activeImgIndex, setActiveImgIndex] = useState(0)

  const getCategoryName = (key: string) =>
    register.categoryOptions.find((o: any) => o.key === key)?.string_value || key || "—"

  const getTypeName = (key: string) =>
    register.itemTypes.find((o: any) => o.key === key)?.string_value || key || "—"

  const getSellPrice = (currItem: TStorageItemRS) =>
    currItem.prices?.find((p: any) => p.type === "SELL")?.value ?? currItem.sell_price

  const getPurchasePrice = (currItem: TStorageItemRS) =>
    currItem.prices?.find((p: any) => p.type === "PURCHASE")?.value ?? currItem.purchase_price

  const formatCOP = (v?: number) =>
    v === undefined ? "—" : `$${v.toLocaleString("es-CO")} COP`

  const renderStock = (inventory: TInventoryFirestore[], register_type: string) => {
    if (register_type === "SERVICE") return <Tag color="default">No aplica</Tag>

    const total = (!inventory || inventory.length === 0)
      ? 0
      : inventory.reduce((s, e) => s + e.quantity, 0)

    let warningIcon = null
    if (total === 0) {
      warningIcon = (
        <Tooltip title="No hay unidades disponibles. Puedes agregarlas desde el módulo de Storage haciendo un movimiento de entrada.">
          <ExclamationCircleOutlined style={{ color: '#faad14', marginLeft: 6 }} />
        </Tooltip>
      )
    } else if (total < 0) {
      warningIcon = (
        <Tooltip title="Se hicieron movimientos sin tener unidades. Debes reabastecer con un movimiento desde el módulo de Storage.">
          <ExclamationCircleOutlined style={{ color: '#f5222d', marginLeft: 6 }} />
        </Tooltip>
      )
    }

    if (!inventory || inventory.length === 0 || inventory.length === 1) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>{total}</span>
          {warningIcon}
        </div>
      )
    }

    const content = (
      <div style={{ minWidth: 180 }}>
        {inventory.map((entry) => {
          const storeName = store.list.find((s: any) => s.uid === entry.uid)?.name || entry.uid
          return (
            <div key={entry.uid} style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "2px 0" }}>
              <span>{storeName}</span>
              <strong>{entry.quantity}</strong>
            </div>
          )
        })}
      </div>
    )
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Popover title="Existencias por almacén" content={content} trigger="hover">
          <span style={{ cursor: "pointer", borderBottom: "1px dashed #1890ff", color: "#1890ff" }}>{total}</span>
        </Popover>
        {warningIcon}
      </div>
    )
  }

  // Sincronizar al abrir
  useEffect(() => {
    const loadFullItem = async () => {
      if (!initialItem?.uid) return
      setSpinning(true)
      try {
        const res = await securePost("/inventory/item/get-item", {
          uidItem: initialItem.uid,
          company: company.current?.uid,
          module: "STORAGE_ITEM_LIST",
        })
        setSpinning(false)
        if (res.status === 200) {
          const fullData = res.inventoryItem || res.data
          setItem(fullData)
        } else {
          message.error(res.message || "Error al cargar el detalle del producto")
          onCancel()
        }
      } catch (err) {
        console.error(err)
        setSpinning(false)
        message.error("Error de red al cargar el producto")
        onCancel()
      }
    }

    if (visible && initialItem) {
      setItem(initialItem)
      setActiveImgIndex(0)
      loadFullItem()
    }
  }, [visible, initialItem?.uid])

  const displayItem = item ?? initialItem

  const productImages = (displayItem?.images && displayItem.images.length > 0)
    ? displayItem.images
    : (displayItem?.image ? [{ file_name: displayItem.name || "Foto", file_url: displayItem.image, is_primary: true, display_order: 0, storage_path: "" }] : [])
  const primaryImage = productImages.find((img: any) => img.is_primary) || productImages[0]
  const activeImage = productImages[activeImgIndex] || primaryImage

  return (
    <Modal
      title={<div style={{ textAlign: "center" }}>{displayItem?.name || "Detalle del elemento"}</div>}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="close" onClick={onCancel}>
          Cerrar
        </Button>
      ]}
      maskClosable={true}
      destroyOnHidden
      width={820}
    >
      <Spin spinning={spinning}>
        {displayItem && (
          <div style={{ padding: "0 4px 8px" }}>
            {/* Encabezado con Nombre e ID */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 20, fontWeight: 600, color: "#262626" }}>{displayItem.name || "—"}</div>
              {displayItem.custom_id && (
                <div style={{ fontSize: 13, color: "#8c8c8c", marginTop: 2 }}>ID: {displayItem.custom_id}</div>
              )}
            </div>

            {/* Galería de Imágenes en Vista Detalle */}
            {productImages.length > 0 ? (
              <div
                style={{
                  background: "#fafafa",
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 16,
                  border: "1px solid #f0f0f0",
                }}
              >
                <div
                  style={{
                    height: 200,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#ffffff",
                    borderRadius: 6,
                    overflow: "hidden",
                    marginBottom: productImages.length > 1 ? 10 : 0,
                  }}
                >
                  <Image
                    src={activeImage?.file_url}
                    alt={displayItem.name}
                    style={{
                      maxHeight: 200,
                      width: "100%",
                      objectFit: "contain",
                      padding: 6,
                    }}
                    preview={{
                      mask: (
                        <Space>
                          <EyeOutlined /> Ampliar imagen
                        </Space>
                      ),
                    }}
                    fallback="assets/images/no-data-image.svg"
                  />
                </div>

                {/* Tiras de miniaturas si hay más de 1 imagen */}
                {productImages.length > 1 && (
                  <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
                    {productImages.map((img: any, idx: number) => (
                      <div
                        key={img.uid || img.id || idx}
                        onClick={() => setActiveImgIndex(idx)}
                        style={{
                          width: 50,
                          height: 50,
                          borderRadius: 4,
                          overflow: "hidden",
                          cursor: "pointer",
                          border: activeImgIndex === idx ? "2px solid #1890ff" : "1px solid #d9d9d9",
                          flexShrink: 0,
                          background: "#ffffff",
                          position: "relative",
                        }}
                      >
                        <img
                          src={img.file_url}
                          alt=""
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                        {img.is_primary && (
                          <div
                            style={{
                              position: "absolute",
                              bottom: 0,
                              right: 0,
                              background: "#faad14",
                              color: "#fff",
                              fontSize: 8,
                              padding: "0 2px",
                              lineHeight: "10px",
                              borderRadius: "2px 0 0 0",
                            }}
                          >
                            ★
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            <div style={{ background: "#fafafa", borderRadius: 8, padding: "4px 16px", marginBottom: 10 }}>
              <InfoRow icon={<AppstoreOutlined />} label="Tipo">
                <Tag color="blue">{getTypeName(displayItem.register_type)}</Tag>
              </InfoRow>

              <InfoRow icon={<TagOutlined />} label="Categoría">
                {getCategoryName(displayItem.category)}
              </InfoRow>

              {displayItem.description && (
                <InfoRow icon={<FileTextOutlined />} label="Descripción">
                  <span style={{ ...valueStyle, fontWeight: 400 }}>{displayItem.description}</span>
                </InfoRow>
              )}

              {displayItem.register_type !== "SERVICE" && (
                <InfoRow icon={<InboxOutlined />} label="Existencias">
                  {renderStock((displayItem.inventory || []) as TInventoryFirestore[], displayItem.register_type)}
                </InfoRow>
              )}

              {displayItem.location && (
                <InfoRow icon={<EnvironmentOutlined />} label="Ubicación">
                  {displayItem.location}
                </InfoRow>
              )}

              {displayItem.barcode && (
                <InfoRow icon={<BarcodeOutlined />} label="Código de barras">
                  {displayItem.barcode}
                </InfoRow>
              )}

              {displayItem.sell_item && (
                <InfoRow icon={<DollarOutlined />} label="Precio de venta">
                  {formatCOP(getSellPrice(displayItem))}
                </InfoRow>
              )}

              {displayItem.purchase_item && (
                <InfoRow icon={<DollarOutlined />} label="Precio de compra">
                  {formatCOP(getPurchasePrice(displayItem))}
                </InfoRow>
              )}

              {displayItem.is_traceable && (
                <InfoRow icon={<DeploymentUnitOutlined style={{ color: "#1d39c4" }} />} label="Trazabilidad">
                  <Tag color="blue">Trazabilidad Individual Habilitada</Tag>
                </InfoRow>
              )}

              {displayItem.other_values?.filter((v: any) => v.is_visible && v.field_name && v.field_value).map((v: any) => (
                <InfoRow key={v.id} icon={<FileTextOutlined />} label={v.field_name}>
                  {v.field_value}
                </InfoRow>
              ))}
            </div>
          </div>
        )}
      </Spin>
    </Modal>
  )
}

export default ViewItemModal
